import { nextReconnectDelayMs } from './hubFactory';

export type CancelBeforeDisposingConnection = () => void;

export type RetryableHubConnection = {
  start: () => Promise<void>;
  onClose: (callback: (error?: Error) => void) => () => void;
};

export type StartHubWithRetryCallbacks = {
  onConnected?: (retriesSurvivedMeaningDataIsStale: number) => void;
  onStable?: () => void;
  onDisconnected?: (error: unknown) => void;
};

export const CONNECTION_HOLDS_AFTER_MS = 3_000;

export function startHubWithRetry(
  connection: RetryableHubConnection,
  callbacks: StartHubWithRetryCallbacks = {},
): CancelBeforeDisposingConnection {
  let active = true;
  let retryCount = 0;
  let retryTimer: ReturnType<typeof setTimeout> | undefined;
  let holdTimer: ReturnType<typeof setTimeout> | undefined;

  const scheduleRetry = (error: unknown) => {
    clearTimeout(holdTimer);
    holdTimer = undefined;

    if (!active) {
      return;
    }

    callbacks.onDisconnected?.(error);

    clearTimeout(retryTimer);
    retryTimer = setTimeout(start, nextReconnectDelayMs(retryCount));
    retryCount += 1;
  };

  function start() {
    void connection.start().then(() => {
      if (!active) {
        return;
      }

      callbacks.onConnected?.(retryCount);

      clearTimeout(holdTimer);
      holdTimer = setTimeout(() => {
        retryCount = 0;
        callbacks.onStable?.();
      }, CONNECTION_HOLDS_AFTER_MS);
    }, scheduleRetry);
  }

  const unsubscribeClosed = connection.onClose(scheduleRetry);
  start();

  return () => {
    active = false;
    unsubscribeClosed();
    clearTimeout(retryTimer);
    clearTimeout(holdTimer);
  };
}
